import { Capacitor } from '@capacitor/core';
import { getToken, setToken, clearToken } from './tokenStore';
import {
  User,
  Headache,
  HeadacheFormData,
  HeadacheStats,
  Tension,
  TensionFormData,
  TensionStats,
  Lloro,
  LloroFormData,
  LloroStats,
  LoginCredentials,
  RegisterData,
  ApiResponse,
  AdminUser,
  AdminDashboardStats,
  AdminHeadache,
  AdminTension,
  AdminLloro,
  ActivityItem,
  PushSubscription,
  SubscriptionInfo,
} from '../types';

// En la web (nginx sirve el build en el mismo dominio) las rutas relativas
// funcionan. Pero dentro del contenedor nativo de Capacitor el HTML se sirve
// desde capacitor://localhost, asi que /api no apunta al backend: hay que usar
// la URL absoluta de produccion. Detectamos la plataforma nativa en tiempo de
// ejecucion para que un mismo build valga para web e iOS/Android.
const API_BASE = Capacitor.isNativePlatform()
  ? 'https://maldolor.com/api'
  : '/api';

// El token se lee del tokenStore (caché en memoria respaldada por Keychain en
// nativo, localStorage en web). getToken/setToken/clearToken se importan arriba.

// Control de refresh: evita múltiples refreshes simultáneos
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const attemptTokenRefresh = async (): Promise<string | null> => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.success && data.data?.token) {
      setToken(data.data.token);
      if (data.data.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }
      return data.data.token;
    }
    return null;
  } catch {
    return null;
  }
};

const refreshToken = async (): Promise<string | null> => {
  // Si ya hay un refresh en curso, esperar a que termine
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = attemptTokenRefresh().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
};

// Base fetch with auth
const fetchWithAuth = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Si 503 offline (del service worker), lanzar error de red para que el frontend guarde offline
  if (response.status === 503) {
    const body = await response.clone().json().catch(() => null);
    if (body?.offline) {
      throw new Error('Failed to fetch');
    }
  }

  // Si 401, intentar refresh y reintentar la petición original
  if (response.status === 401) {
    const newToken = await refreshToken();

    if (newToken) {
      // Reintentar con el nuevo token
      const retryHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${newToken}`,
        ...options.headers,
      };

      return fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: retryHeaders,
      });
    }

    // Refresh fallo: limpiar sesion
    // No hacer redirect aqui - dejar que AuthContext maneje el estado
    clearToken();
    localStorage.removeItem('user');
    // Limpiar cache del API en el SW
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
    }
    // Solo redirigir si no estamos ya en login
    if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
      window.location.href = '/login';
    }
  }

  return response;
};

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    return response.json();
  },

  register: async (data: RegisterData): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  logout: async (): Promise<void> => {
    // El backend solo registra el evento en el audit log (JWT es stateless)
    await fetchWithAuth('/auth/logout', { method: 'POST' });
    clearToken();
    localStorage.removeItem('user');
  },

  // El endpoint del backend es /auth/me (no /auth/profile)
  getProfile: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await fetchWithAuth('/auth/me');
    return response.json();
  },
};

// Impersonation API (solo admin)
export const impersonationAPI = {
  start: async (userId: number): Promise<ApiResponse<{
    impersonationToken: string;
    targetUser: { id: number; name: string; email: string; avatar_url?: string };
    admin: { id: number; name: string; email: string };
  }>> => {
    const response = await fetchWithAuth(`/impersonation/start/${userId}`, { method: 'POST' });
    return response.json();
  },

  stop: async (): Promise<ApiResponse<{ adminToken: string; admin: User }>> => {
    const response = await fetchWithAuth('/impersonation/stop', { method: 'POST' });
    return response.json();
  },
};

// Passkeys (WebAuthn) API
export interface PasskeyDevice {
  id: number;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string;
  /** true si la passkey esta sincronizada en iCloud o en el gestor de Google */
  syncedToCloud: boolean;
  isMultiDevice: boolean;
}

export const passkeysAPI = {
  getStatus: async (): Promise<ApiResponse<{ hasCredentials: boolean; credentialCount: number }>> => {
    const response = await fetchWithAuth('/biometric/status');
    return response.json();
  },

  registerBegin: async (): Promise<ApiResponse<any>> => {
    const response = await fetchWithAuth('/biometric/register/begin', { method: 'POST' });
    return response.json();
  },

  registerComplete: async (credential: any, deviceName?: string): Promise<ApiResponse<any>> => {
    const response = await fetchWithAuth('/biometric/register/complete', {
      method: 'POST',
      body: JSON.stringify({ credential, deviceName }),
    });
    return response.json();
  },

  // El login con passkey no lleva token ni email: el servidor identifica al
  // usuario por el credential_id que devuelve el autenticador.
  authenticateBegin: async (): Promise<ApiResponse<any>> => {
    const response = await fetch(`${API_BASE}/biometric/authenticate/begin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    return response.json();
  },

  authenticateComplete: async (credential: any): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await fetch(`${API_BASE}/biometric/authenticate/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
    return response.json();
  },

  getDevices: async (): Promise<ApiResponse<{ devices: PasskeyDevice[] }>> => {
    const response = await fetchWithAuth('/biometric/devices');
    return response.json();
  },

  deleteDevice: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/biometric/devices/${id}`, { method: 'DELETE' });
    return response.json();
  },
};

// Headaches API
export const headachesAPI = {
  getAll: async (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<{ headaches: Headache[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);

    const response = await fetchWithAuth(`/headaches?${queryParams}`);
    return response.json();
  },

  getById: async (id: number): Promise<ApiResponse<{ headache: Headache }>> => {
    const response = await fetchWithAuth(`/headaches/${id}`);
    return response.json();
  },

  create: async (data: HeadacheFormData): Promise<ApiResponse<{ headache: Headache }>> => {
    const response = await fetchWithAuth('/headaches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id: number, data: Partial<HeadacheFormData>): Promise<ApiResponse<{ headache: Headache }>> => {
    const response = await fetchWithAuth(`/headaches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/headaches/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  getStats: async (): Promise<ApiResponse<{ stats: HeadacheStats }>> => {
    const response = await fetchWithAuth('/headaches/stats');
    return response.json();
  },

  // Medicamentos que el usuario ya ha registrado, de mas a menos usados
  getMedications: async (): Promise<ApiResponse<{
    medications: { name: string; uses: number; lastUsed: string }[];
  }>> => {
    const response = await fetchWithAuth('/headaches/medications');
    return response.json();
  },
};

// Tensions API
export const tensionsAPI = {
  getAll: async (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<{ tensions: Tension[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);

    const response = await fetchWithAuth(`/tensions?${queryParams}`);
    return response.json();
  },

  getById: async (id: number): Promise<ApiResponse<{ tension: Tension }>> => {
    const response = await fetchWithAuth(`/tensions/${id}`);
    return response.json();
  },

  create: async (data: TensionFormData): Promise<ApiResponse<{ tension: Tension }>> => {
    const response = await fetchWithAuth('/tensions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id: number, data: Partial<TensionFormData>): Promise<ApiResponse<{ tension: Tension }>> => {
    const response = await fetchWithAuth(`/tensions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/tensions/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  getStats: async (): Promise<ApiResponse<{ stats: TensionStats }>> => {
    const response = await fetchWithAuth('/tensions/stats');
    return response.json();
  },
};

// Lloros API
export const llorosAPI = {
  getAll: async (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<{ lloros: Lloro[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);

    const response = await fetchWithAuth(`/lloros?${queryParams}`);
    return response.json();
  },

  getById: async (id: number): Promise<ApiResponse<{ lloro: Lloro }>> => {
    const response = await fetchWithAuth(`/lloros/${id}`);
    return response.json();
  },

  create: async (data: LloroFormData): Promise<ApiResponse<{ lloro: Lloro }>> => {
    const response = await fetchWithAuth('/lloros', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id: number, data: Partial<LloroFormData>): Promise<ApiResponse<{ lloro: Lloro }>> => {
    const response = await fetchWithAuth(`/lloros/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/lloros/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  getStats: async (): Promise<ApiResponse<{ stats: LloroStats }>> => {
    const response = await fetchWithAuth('/lloros/stats');
    return response.json();
  },

  canAccess: async (): Promise<ApiResponse<{ canAccess: boolean }>> => {
    const response = await fetchWithAuth('/lloros/can-access');
    return response.json();
  },
};

// Reminder type
export interface Reminder {
  id: number;
  user_id: number;
  type: 'tension' | 'headache' | 'medication';
  title: string;
  message?: string;
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  time: string;
  start_date?: string;
  end_date?: string;
  notification_methods: string[];
  enabled: boolean;
  last_sent?: string;
  next_send?: string;
  created_at?: string;
}

export interface ReminderFormData {
  type: 'tension' | 'headache' | 'medication';
  title: string;
  message?: string;
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  time: string;
  start_date?: string;
  end_date?: string;
  notification_method: 'push' | 'email' | 'both';
  is_active?: boolean;
}

// Reminders API
export const remindersAPI = {
  getAll: async (): Promise<ApiResponse<{ reminders: Reminder[] }>> => {
    const response = await fetchWithAuth('/reminders');
    return response.json();
  },

  create: async (data: ReminderFormData): Promise<ApiResponse<{ reminder: Reminder }>> => {
    const response = await fetchWithAuth('/reminders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id: number, data: Partial<ReminderFormData>): Promise<ApiResponse<{ reminder: Reminder }>> => {
    const response = await fetchWithAuth(`/reminders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/reminders/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  toggle: async (id: number): Promise<ApiResponse<{ reminder: Reminder }>> => {
    const response = await fetchWithAuth(`/reminders/${id}/toggle`, {
      method: 'PATCH',
    });
    return response.json();
  },
};

// Export API
export const exportAPI = {
  exportData: async (): Promise<any> => {
    const response = await fetchWithAuth('/users/export');
    return response.json();
  },

  // Generar informe médico PDF (disponible para todos los usuarios)
  generateReport: async (params: { type: 'dolores' | 'tensiones' | 'ambos'; startDate: string; endDate: string }): Promise<Blob> => {
    const token = getToken();
    const queryParams = new URLSearchParams({
      type: params.type,
      startDate: params.startDate,
      endDate: params.endDate,
    });

    const response = await fetch(`${API_BASE}/users/report/pdf?${queryParams}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Error al generar el informe');
    }

    return response.blob();
  },
};

// Subscription API
export const subscriptionAPI = {
  getPlans: async (): Promise<ApiResponse<{ plans: any[] }>> => {
    const response = await fetchWithAuth('/subscriptions/plans');
    return response.json();
  },

  getCurrent: async (): Promise<ApiResponse<{ subscription: any }>> => {
    const response = await fetchWithAuth('/subscriptions/current');
    return response.json();
  },

  getUsage: async (): Promise<ApiResponse<{ usage: any }>> => {
    const response = await fetchWithAuth('/subscriptions/usage');
    return response.json();
  },

  getTrialInfo: async (): Promise<ApiResponse<{ data: SubscriptionInfo } & SubscriptionInfo>> => {
    const response = await fetchWithAuth('/subscriptions/trial-info');
    return response.json();
  },

  checkFeature: async (featureName: string): Promise<ApiResponse<{ hasFeature: boolean }>> => {
    const response = await fetchWithAuth(`/subscriptions/feature/${featureName}`);
    return response.json();
  },

  checkLimit: async (limitType: string): Promise<ApiResponse<any>> => {
    const response = await fetchWithAuth(`/subscriptions/limits/${limitType}`);
    return response.json();
  },
};

// Users API
export const usersAPI = {
  updateProfile: async (data: { name?: string; phone?: string }): Promise<ApiResponse<{ user: User }>> => {
    const response = await fetchWithAuth('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  uploadPhoto: async (file: File): Promise<ApiResponse<{ profile_photo: string }>> => {
    const token = getToken();
    const formData = new FormData();
    formData.append('photo', file);

    const response = await fetch(`${API_BASE}/users/upload-photo`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    return response.json();
  },
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboard: async (): Promise<ApiResponse<{ statistics: AdminDashboardStats }>> => {
    const response = await fetchWithAuth('/admin/dashboard');
    return response.json();
  },

  // Activity
  getActivity: async (limit = 20): Promise<ApiResponse<{ activity: ActivityItem[] }>> => {
    const response = await fetchWithAuth(`/admin/activity?limit=${limit}`);
    return response.json();
  },

  // Users
  getUsers: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<{ users: AdminUser[]; pagination: { limit: number; offset: number; total: number } }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const response = await fetchWithAuth(`/admin/users?${queryParams}`);
    return response.json();
  },

  getUser: async (id: number): Promise<ApiResponse<{ user: AdminUser; stats: any }>> => {
    const response = await fetchWithAuth(`/admin/users/${id}`);
    return response.json();
  },

  updateUser: async (id: number, data: Partial<AdminUser>): Promise<ApiResponse<{ user: AdminUser }>> => {
    const response = await fetchWithAuth(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteUser: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/admin/users/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  toggleAdmin: async (id: number): Promise<ApiResponse<{ user: AdminUser }>> => {
    const response = await fetchWithAuth(`/admin/users/${id}/toggle-admin`, {
      method: 'POST',
    });
    return response.json();
  },

  resetUserPassword: async (id: number): Promise<ApiResponse<{ tempPassword: string; instructions: string }>> => {
    const response = await fetchWithAuth(`/admin/users/${id}/reset-password`, {
      method: 'POST',
    });
    return response.json();
  },

  // Headaches
  getHeadaches: async (params?: { limit?: number; offset?: number; search?: string; intensity?: string; startDate?: string; endDate?: string }): Promise<ApiResponse<{ headaches: AdminHeadache[]; pagination: { limit: number; offset: number; total: number } }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.intensity) queryParams.set('intensity', params.intensity);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const response = await fetchWithAuth(`/admin/headaches?${queryParams}`);
    return response.json();
  },

  // Tensions
  getTensions: async (params?: { limit?: number; offset?: number; search?: string; startDate?: string; endDate?: string }): Promise<ApiResponse<{ tensions: AdminTension[]; pagination: { limit: number; offset: number; total: number } }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const response = await fetchWithAuth(`/admin/tensions?${queryParams}`);
    return response.json();
  },

  // Lloros
  getLloros: async (params?: { limit?: number; offset?: number; search?: string; startDate?: string; endDate?: string }): Promise<ApiResponse<{ lloros: AdminLloro[]; pagination: { limit: number; offset: number; total: number } }>> => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const response = await fetchWithAuth(`/admin/lloros?${queryParams}`);
    return response.json();
  },

  // Push Subscriptions
  getPushSubscriptions: async (): Promise<ApiResponse<{ subscriptions: PushSubscription[]; stats: { total: number; unique_users: number } }>> => {
    const response = await fetchWithAuth('/admin/push-subscriptions');
    return response.json();
  },

  deletePushSubscription: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/admin/push-subscriptions/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  // Send push notification
  sendPushNotification: async (data: { userId?: number; title: string; body: string; url?: string }): Promise<ApiResponse<{ sent: number }>> => {
    const response = await fetchWithAuth('/admin/push/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Reminders
  getAllReminders: async (): Promise<ApiResponse<{ reminders: any[] }>> => {
    const response = await fetchWithAuth('/admin/reminders');
    return response.json();
  },

  updateReminder: async (id: number, data: { title: string; message?: string; type: string; frequency: string; time: string; enabled: boolean }): Promise<ApiResponse<{ reminder: any }>> => {
    const response = await fetchWithAuth(`/admin/reminders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteReminder: async (id: number): Promise<ApiResponse<null>> => {
    const response = await fetchWithAuth(`/admin/reminders/${id}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  // Reports
  getIntensityReport: async (params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<{ report: any[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const response = await fetchWithAuth(`/admin/reports/intensity?${queryParams}`);
    return response.json();
  },

  // Audit Logs
  getAuditLogs: async (params?: { action?: string; entityType?: string; limit?: number; offset?: number }): Promise<ApiResponse<{ logs: any[] }>> => {
    const queryParams = new URLSearchParams();
    if (params?.action) queryParams.set('action', params.action);
    if (params?.entityType) queryParams.set('entityType', params.entityType);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const response = await fetchWithAuth(`/admin/audit?${queryParams}`);
    return response.json();
  },
};
